import { Contract } from '@/modules/Contract/types'
import PermanentAddress from '@/modules/Employee/components/Confirmation/PermanentAddress'
import { Path, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Typogaphy, GridHookForm, Button } from 'vnetwork-ui'
import { GridHookFormProps } from 'vnetwork-ui/dist/components/GridHookForm'
import { getCustomerById } from '@/modules/Customer/services'
import { useState } from 'react'
import { User } from 'lucide-react'

interface CustomerInformationProps {
    rootName: string
}

const CustomerInformation: React.FC<CustomerInformationProps> = ({
    rootName,
}) => {
    const { t } = useTranslation()
    const formControl = useFormContext<Contract>()
    const [isPrefilling, setIsPrefilling] = useState(false)

    const forRegister = useWatch({
        control: formControl.control,
        name: `${rootName}.domain_extra_fields.for` as Path<Contract>,
    })

    const customer_id = useWatch({
        control: formControl.control,
        name: 'customer_id',
    })

    const handlePrefillFromCustomer = async () => {
        if (!customer_id) {
            return
        }

        try {
            setIsPrefilling(true)
            const response = await getCustomerById(customer_id)
            const customer = response?.result

            if (!customer) return

            // Prefill owner information from customer data
            formControl.setValue(
                `${rootName}.domain_extra_fields.ownerName` as Path<Contract>,
                customer.customer_name || '',
                { shouldValidate: true }
            )
            formControl.setValue(
                `${rootName}.domain_extra_fields.ownerEmail1` as Path<Contract>,
                customer.customer_email || '',
                { shouldValidate: true }
            )
            formControl.setValue(
                `${rootName}.domain_extra_fields.ownerPhone` as Path<Contract>,
                customer.customer_phone || '',
                { shouldValidate: true }
            )

            // If company exists, prefill tax code for business registration
            if (forRegister === 'doanhnghiep' && customer.company) {
                formControl.setValue(
                    `${rootName}.domain_extra_fields.ownerTaxCode` as Path<Contract>,
                    customer.company.company_tax || '',
                    { shouldValidate: true }
                )
            }
        } catch (error) {
            console.error('Failed to prefill customer info:', error)
        } finally {
            setIsPrefilling(false)
        }
    }

    const renderForm = (
        forRegister: string
    ): Pick<GridHookFormProps<Contract>, 'data'> => {
        return {
            data:
                forRegister === 'canhan'
                    ? [
                          {
                              formItemProps: {
                                  name: `${rootName}.domain_extra_fields.ownerID_Number` as Path<Contract>,
                                  type: 'textfield',
                                  label: t('ownerID_Number'),
                                  placeholder: t('ownerID_Number'),
                              },
                              className: 'col-span-2',
                          },
                      ]
                    : [
                          {
                              formItemProps: {
                                  name: `${rootName}.domain_extra_fields.ownerTaxCode` as Path<Contract>,
                                  type: 'textfield',
                                  label: t('ownerTaxCode'),
                                  placeholder: t('ownerTaxCode'),
                              },
                              className: 'col-span-2',
                          },
                      ],
        }
    }

    return (
        <div data-tour="vnnic-customer">
            <div className="flex items-center justify-between mb-2">
                <Typogaphy className="font-medium" size="medium">
                    {t('customer-information')}
                </Typogaphy>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={handlePrefillFromCustomer}
                    disabled={!customer_id || isPrefilling}
                    loading={isPrefilling}
                    startIcon={<User size={16} />}
                >
                    {t('prefill-from-customer')}
                </Button>
            </div>

            <GridHookForm
                formControl={formControl}
                subForm
                actions={[]}
                data={[
                    {
                        formItemProps: {
                            name: `${rootName}.domain_extra_fields.ownerName` as Path<Contract>,
                            type: 'textfield',
                            label: t('ownerName'),
                            placeholder: t('ownerName'),
                        },
                        className: 'col-span-2',
                    },
                    ...(renderForm(forRegister)?.data || []),
                    {
                        renderEditComponent: () => {
                            return (
                                <PermanentAddress
                                    // @ts-ignore
                                    city_key={`${rootName}.domain_extra_fields.ownerProvince`}
                                    // @ts-ignore
                                    district_key={`${rootName}.domain_extra_fields.ownerDistrict`}
                                    // @ts-ignore
                                    ward_key={`${rootName}.domain_extra_fields.ownerWard`}
                                    // @ts-ignore
                                    steet_key={`${rootName}.domain_extra_fields.ownerAddress`}
                                    // @ts-ignore
                                    country_key={`${rootName}.domain_extra_fields.ownerCountry`}
                                    padding={false}
                                    returnFullOption
                                    renderLabel={(value: any) => {
                                        return value?.name || value?.label
                                    }}
                                    valueMapper={(value: any) => {
                                        if (
                                            !value ||
                                            !value?.label ||
                                            !value?.value
                                        )
                                            throw new Error(
                                                'Something went wrong'
                                            )

                                        return {
                                            label: value?.label,
                                            value: value?.value,
                                        }
                                    }}
                                />
                            )
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: `${rootName}.domain_extra_fields.ownerEmail1` as Path<Contract>,
                            type: 'textfield',
                            label: t('ownerEmail1'),
                            placeholder: t('ownerEmail1'),
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: `${rootName}.domain_extra_fields.ownerEmail2` as Path<Contract>,
                            type: 'textfield',
                            label: t('ownerEmail2'),
                            placeholder: t('ownerEmail2'),
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: `${rootName}.domain_extra_fields.ownerPhone` as Path<Contract>,
                            type: 'textfield',
                            label: t('ownerPhone'),
                            placeholder: t('ownerPhone'),
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: `${rootName}.domain_extra_fields.ownerFax` as Path<Contract>,
                            type: 'textfield',
                            label: t('ownerFax'),
                            placeholder: t('ownerFax'),
                        },
                        className: 'col-span-2',
                    },
                ]}
            />
        </div>
    )
}

export default CustomerInformation
